import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import dayjs, { Dayjs } from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import isBetween from 'dayjs/plugin/isBetween';
import {
  ProviderAvailability,
  RescheduledOrNewReservedVisit,
  SelectedVisit,
  TimeSlot,
} from '@types';
import { TimeOfDay, useFilters } from './components';
import {
  cancelReservation,
  cancelReservationAuthed,
  reserveNewVisitAvailableTime,
  reserveVisit,
} from '@api';
import { useApplicationStore } from '@data';
import { formatDateToISO8601 } from '@utils';
import { RescheduleReason } from '@router/utils';
import { PatientSubscription } from '@qmd-types/patient';

dayjs.extend(customParseFormat);
dayjs.extend(isBetween);

type GroupedTimes = {
  morning: string[];
  afternoon: string[];
  evening: string[];
};

export function useGroupAvailableTimesByTimeOfDay(
  times?: string[],
): GroupedTimes {
  const { hour, timeOfDay } = useFilters();
  const [searchParams] = useSearchParams();
  const date = searchParams.get('date');

  return useMemo(() => {
    if (!date || !times) {
      return {
        morning: [],
        afternoon: [],
        evening: [],
      };
    }

    if (hour && timeOfDay) {
      return groupTimesByHour(times, date, timeOfDay, hour);
    }

    const groupedTimes = times.reduce<GroupedTimes>(
      (acc, time) => {
        const timeSlotTime = getTimeSlotTime(time, date);

        if (!timeSlotTime) return acc;
        if (
          timeSlotTime.hour() < 12 &&
          (timeOfDay === TimeOfDay.MORNING || !timeOfDay)
        ) {
          acc.morning.push(time);
        } else if (
          timeSlotTime.hour() >= 12 &&
          timeSlotTime.hour() <= 18 &&
          (timeOfDay === TimeOfDay.AFTERNOON || !timeOfDay)
        ) {
          acc.afternoon.push(time);
        } else if (
          (timeSlotTime.hour() >= 18 && timeOfDay === TimeOfDay.EVENING) ||
          !timeOfDay
        ) {
          acc.evening.push(time);
        }

        return acc;
      },
      {
        morning: [],
        afternoon: [],
        evening: [],
      },
    );

    return groupedTimes;
  }, [date, hour, timeOfDay, times]);
}

function groupTimesByHour(
  times: string[],
  date: string,
  timeOfDay: TimeOfDay,
  hour: number,
): GroupedTimes {
  const groupedTimes: GroupedTimes = {
    morning: [],
    afternoon: [],
    evening: [],
  };

  const { startTime, endTime } = getStartAndEndTimesByHour(date, hour);
  const filteredTimes = times.filter((time) => {
    const timeSlotTime = getTimeSlotTime(time, date);
    if (!timeSlotTime) return false;
    return timeSlotTime.isBetween(startTime, endTime, null, '[)');
  });

  if (timeOfDay === TimeOfDay.MORNING) {
    groupedTimes.morning = filteredTimes;
  } else if (timeOfDay === TimeOfDay.AFTERNOON) {
    groupedTimes.afternoon = filteredTimes;
  } else if (timeOfDay === TimeOfDay.EVENING) {
    groupedTimes.evening = filteredTimes;
  }

  return groupedTimes;
}

export function getStartAndEndTimes(
  timeOfDay: TimeOfDay,
  date: string,
  hour: number | null,
): { startTime: Dayjs; endTime: Dayjs } {
  if (hour) {
    return getStartAndEndTimesByHour(date, hour);
  }

  const baseDate = dayjs(date).startOf('day');

  if (timeOfDay === TimeOfDay.MORNING) {
    return {
      startTime: baseDate,
      endTime: baseDate.hour(12),
    };
  }

  if (timeOfDay === TimeOfDay.AFTERNOON) {
    return {
      startTime: baseDate.hour(12),
      endTime: baseDate.hour(18),
    };
  }

  return {
    startTime: baseDate.hour(18),
    endTime: baseDate.endOf('day'),
  };
}

function getStartAndEndTimesByHour(date: string, hour: number) {
  const baseDate = dayjs(date);

  return {
    startTime: baseDate.set('hour', hour),
    endTime: baseDate.set('hour', hour + 1),
  };
}

export function getTimeSlotTime(time: string, date: string) {
  const [hours, minutes] = time.split(':').map(Number);
  return dayjs(date).set('hour', hours).set('minute', minutes);
}

export function getTimeSlotByProviderId(
  availabilities: ProviderAvailability[],
  providerId: string,
  date: string,
): TimeSlot {
  const providerAvailability = availabilities.find(
    (availability) => availability.provider.id === providerId,
  );

  if (
    !providerAvailability ||
    providerAvailability.timeSlotCollection[0].date !== date
  ) {
    return {
      times: [],
      date: '',
    };
  }

  const { timeSlotCollection } = providerAvailability;

  return timeSlotCollection[0];
}

export async function releaseVisitIfReserved() {
  const { auth, reservedVisit, setReservedVisit, setVisit } =
    useApplicationStore.getState();
  const { accessToken } = auth;

  try {
    if (accessToken && reservedVisit) {
      // Once a reservation has been assigned to an authenticated user, we must use the authenticated cancel api
      await cancelReservationAuthed(reservedVisit.reservationId);
    } else if (reservedVisit) {
      await cancelReservation(reservedVisit.reservationId);
    }
  } catch (error) {
    console.log(error);
  }

  setReservedVisit(undefined);
  setVisit(null);
}

export async function handleSelectVisit({
  date,
  provider,
  time,
  visitTypeId,
  timeZone,
  visitId,
}: SelectedVisit) {
  const { auth, setVisit, verificationToken, stateCode, setReservedVisit } =
    useApplicationStore.getState();
  const { accessToken } = auth;

  await releaseVisitIfReserved();

  setVisit({
    date,
    provider,
    time,
    timeZone,
    visitTypeId,
    visitId,
  });

  let newReservedVisit: RescheduledOrNewReservedVisit | null = null;
  const baseParams = {
    dateTime: formatDateToISO8601(dayjs(`${date} ${time}`).toDate()),
    providerId: provider.id,
    timeZone,
    visitTypeId,
    visitId,
  };

  if (accessToken) {
    newReservedVisit = await reserveNewVisitAvailableTime(
      baseParams,
      visitId ? 'reschedulevisit' : 'newvisit',
    );
  } else if (verificationToken) {
    newReservedVisit = await reserveVisit({
      ...baseParams,
      phoneNumberVerificationToken: verificationToken,
      stateCode: stateCode!,
    });
  }

  if (newReservedVisit) {
    setReservedVisit(newReservedVisit);
  }
}

export function getNextStepForRescheduleReason(
  reason: RescheduleReason | null,
  isLoggedIn: boolean,
  notReadyToBook?: boolean,
) {
  if (isLoggedIn || reason === 'userInitiated') {
    return notReadyToBook ? '/booking-confirmation' : '/complete-booking';
  } else if (reason === 'dateTimeNotAvailable') {
    return '/complete-profile';
  } else {
    return '/email';
  }
}

const MHT_SERVICE_TYPE_ID = '8cbfd504-8a32-4074-91cb-950741f3b443';

export const serviceIsMht = (serviceTypeId: string) =>
  serviceTypeId === MHT_SERVICE_TYPE_ID;

const ADDICTION_TREATMENT_SERVICE_TYPE_IDS: string[] = [
  '3', // production
  'f492bba8-89b7-4e33-aeda-f8c24852864e', // dev/QA
];

export const serviceIsAddictionTreatment = (serviceTypeId: string) =>
  ADDICTION_TREATMENT_SERVICE_TYPE_IDS.includes(serviceTypeId);

const WEIGHT_LOSS_SERVICE_TYPE_ID = '1';
const URGENT_CARE_SERVICE_TYPE_ID = '6';
export const serviceIsWeightLossOrUrgentCare = (serviceTypeId: string) =>
  [WEIGHT_LOSS_SERVICE_TYPE_ID, URGENT_CARE_SERVICE_TYPE_ID].includes(
    serviceTypeId,
  );

export const sortPatientSubscriptionsByActiveAndMostRecent = (
  patientSubscriptions: PatientSubscription[],
) =>
  patientSubscriptions.slice().sort((a, b) => {
    // Null endDateUtc comes first
    if (a.endDateUtc === null && b.endDateUtc !== null) return -1;
    if (a.endDateUtc !== null && b.endDateUtc === null) return 1;

    // If both have endDateUtc, sort by endDateUtc descending (larger first)
    if (a.endDateUtc !== null && b.endDateUtc !== null) {
      const endDateDiff =
        new Date(b.endDateUtc).getTime() - new Date(a.endDateUtc).getTime();
      if (endDateDiff !== 0) return endDateDiff;
    }

    // Finally, sort by StartDateUtc descending (larger first)
    return (
      new Date(b.startDateUtc).getTime() - new Date(a.startDateUtc).getTime()
    );
  });
